/**
 * Единая точка истины для баннеров сцен в чате ассистента.
 *
 * При получении картинки с `moveToSceneId` от backend компонент
 * `MarkdownBlock` определяет тип баннера через `getSceneBannerKind`:
 *   - `artifact` — PoiArtifact-overlay (Чёрный камень, макам Ибрахима, …).
 *     Рендерится `SceneImageCard` (вертикальная карточка с бейджем на фото).
 *   - `inside`   — inside-сцена (kaaba-inside, …). Рендерится
 *     `SceneBannerCard` (горизонтальная карточка с кнопкой).
 *   - `main`     — основная сцена. Backend присылает её id в форме
 *     poi-filter (`main` / `arafat-scene` / `mina-scene` /
 *     `muzdalifa-scene`); `MAIN_BANNER_SCENE_ID_MAP` разворачивает
 *     это в URL-id (`kaaba` / `arafat` / `mina` / `muzdalifah`),
 *     которые принимает `enterScene`. Рендерится тем же
 *     `SceneBannerCard`.
 *
 * Текст кнопки задаётся через `SCENE_BANNER_BUTTON_LABEL_KEYS` —
 * это i18n-ключ внутри `messages.assistant`. main и inside используют
 * общий шаблон `exploreLocation` ("Explore {name}"). Имя локации
 * (для подстановки в `{name}`) ищется через `resolveBannerLocationKey`:
 * для `main` — id основной сцены, для `inside` — id её родителя.
 * Оба варианта лежат в `globalMap.markerLabels`. Чтобы поменять
 * надпись — правишь либо ключ в `SCENE_BANNER_BUTTON_LABEL_KEYS`,
 * либо сам перевод в `i18n/messages.ts`.
 */
import type { Messages } from '../i18n/messages';
import { isInsideScene, INSIDE_SCENE_PARENT } from './insideScenes/registry';
import { isEraScene, isStubEraScene, ERA_SCENE_PARENT } from './eraScenes/registry';
import { isPoiArtifact } from './poiArtifacts/registry';
import { IS_BALADY } from '../config/appConfig';
import { KNOWN_SCENES } from './sceneNavigationMap';

export type SceneBannerKind = 'artifact' | 'inside' | 'main';

/**
 * Backend для основных сцен присылает не URL-id (`kaaba`/`arafat`/...),
 * а poi-filter id: `main` (Кааба), `arafat-scene`, `mina-scene`,
 * `muzdalifa-scene`. Мапа разворачивает их в URL-id, которые принимает
 * `enterScene` / `splatRegistry`. `overall` сюда не входит — root-карта
 * Мекки, переход на неё из чата контекстно бессмысленен.
 *
 * Если backend начнёт присылать другую форму id — добавь сюда новые
 * ключи; обратное направление (URL-id → poi-filter id) живёт в
 * `SCENE_POI_ID` (`sceneNavigationMap.ts`).
 */
export const MAIN_BANNER_SCENE_ID_MAP: Record<string, string> = {
  main: 'kaaba',
  'arafat-scene': 'arafat',
  'mina-scene': 'mina',
  'muzdalifa-scene': 'muzdalifah',
};

export const isMainBannerScene = (sceneId: string): boolean =>
  sceneId in MAIN_BANNER_SCENE_ID_MAP;

export const resolveMainBannerSceneUrlId = (sceneId: string): string | null =>
  MAIN_BANNER_SCENE_ID_MAP[sceneId] ?? null;

type AssistantStringKey = {
  [K in keyof Messages['assistant']]: Messages['assistant'][K] extends string ? K : never;
}[keyof Messages['assistant']];

/**
 * `exploreLocation` — шаблон "Explore {name}". Имя подставляется через
 * `resolveBannerLocationKey` (см. ниже).
 * `openScene3D` — простая строка без подстановок.
 */
export const SCENE_BANNER_BUTTON_LABEL_KEYS: Record<SceneBannerKind, AssistantStringKey> = {
  artifact: 'openScene3D',
  inside: 'exploreLocation',
  main: 'exploreLocation',
};

/**
 * Возвращает ключ для лукапа имени локации в `globalMap.markerLabels`.
 * Для `main` — URL-id основной сцены, для `inside` — id её родителя
 * (inside-сцены в `markerLabels` не дублируются: "Explore the Kaaba"
 * для `kaaba-inside` приходит через ключ `kaaba`). Для `artifact`
 * возвращает `null` — у него шаблон без подстановки.
 */
export const resolveBannerLocationKey = (
  kind: SceneBannerKind,
  sceneId: string,
): string | null => {
  if (kind === 'main') return resolveMainBannerSceneUrlId(sceneId);
  if (kind === 'inside') return INSIDE_SCENE_PARENT[sceneId] ?? null;
  return null;
};

export const getSceneBannerKind = (sceneId: string): SceneBannerKind | null => {
  if (isPoiArtifact(sceneId)) return 'artifact';
  if (isInsideScene(sceneId)) return 'inside';
  if (isMainBannerScene(sceneId)) return 'main';
  return null;
};

/**
 * Иконки локаций для золотой "Next scene" кнопки в `PoiChips`. Ключ — URL-id
 * основной сцены. Файлы лежат в `public/locations/<id>.png`. Если появится
 * новая основная сцена — добавь её сюда; для inside- и era-сцен иконка
 * наследуется от родителя через `INSIDE_SCENE_PARENT` / `ERA_SCENE_PARENT`.
 */
const SCENE_ICON_MAP: Record<string, string> = {
  kaaba: '/locations/kaaba.png',
  arafat: '/locations/arafat.png',
  mina: '/locations/mina.png',
  muzdalifah: '/locations/muzdalifah.png',
};

export type NextSceneTarget = {
  /** Sce id для передачи в `handleEnterScene` — в виде, ожидаемом dispatcher'ом. */
  sceneId: string;
  /** Ключ для `i18n.globalMap.markerLabels` (подстановка `{name}` в шаблон). */
  labelKey: string;
  /** Путь к PNG-иконке или `null`, если не нашлась. */
  iconSrc: string | null;
};

/**
 * Нормализует `scene_id` из `tour: [{ kind: 'scene' }]` в форму, пригодную
 * для рендера и навигации. Backend может прислать любой из четырёх форматов:
 *
 *   - URL-id основной сцены: `kaaba` / `arafat` / `mina` / `muzdalifah`
 *   - backend poi-filter id: `main` / `arafat-scene` / `mina-scene` /
 *     `muzdalifa-scene`
 *   - inside-id: `kaaba-inside` / `kaaba-inside-scene`
 *   - era-id: `kaaba-2000bce` / `kaaba-600ce` / `kaaba-1890s` / `kaaba-2030`
 *
 * Возвращает `null`, если id не распознан (тогда кнопку Next scene не
 * показываем — лучше тихо игнорировать, чем рендерить нерабочую).
 *
 * Для navigation `sceneId` отдаётся в форме, которую `AssistantHud.handleEnterScene`
 * умеет обрабатывать:
 *   - main backend-id (`main` / `arafat-scene` / ...) — пропускаем как есть,
 *     `resolveMainBannerSceneUrlId` развернёт его в URL-id внутри
 *     `handleEnterScene`. В labelKey кладём URL-id (для `markerLabels`).
 *   - inside-id — как есть (`handleEnterScene` ветка `isInsideScene`).
 *     labelKey = id родителя из `INSIDE_SCENE_PARENT`.
 *   - era-id — как есть (`handleEnterScene` ветка `isEraScene` →
 *     `navigateToHistoricalScene`). labelKey = сам id (`markerLabels`
 *     содержит эры), иконка родителя из `ERA_SCENE_PARENT`. Stub-эры
 *     (нет сцены/бэкенда) и Balady-сборка (`IS_BALADY`) → `null`.
 *   - URL-id основной сцены — нужно конвертировать в backend-id, чтобы
 *     `handleEnterScene` зашёл в main-ветку (URL-id не входит в
 *     `MAIN_BANNER_SCENE_ID_MAP`). Поэтому ищем обратный мапинг.
 *     labelKey = URL-id.
 */
const URL_ID_TO_BACKEND: Record<string, string> = Object.fromEntries(
  Object.entries(MAIN_BANNER_SCENE_ID_MAP).map(([backend, urlId]) => [urlId, backend]),
);

export const resolveNextSceneTarget = (sceneId: string): NextSceneTarget | null => {
  // 1. Backend poi-filter id (main / arafat-scene / ...)
  const mainUrlId = resolveMainBannerSceneUrlId(sceneId);
  if (mainUrlId) {
    return {
      sceneId,
      labelKey: mainUrlId,
      iconSrc: SCENE_ICON_MAP[mainUrlId] ?? null,
    };
  }
  // 2. Inside-scene id
  if (isInsideScene(sceneId)) {
    const parent = INSIDE_SCENE_PARENT[sceneId] ?? null;
    return {
      sceneId,
      labelKey: parent ?? sceneId,
      iconSrc: parent ? SCENE_ICON_MAP[parent] ?? null : null,
    };
  }
  // 3. Era-scene id — как есть; подпись лежит в `markerLabels` под самим id,
  // иконка — родительская. Не показываем stub-эры (нет сцены/бэкенда) и любые
  // эры в Balady-сборке — там историческая фича скрыта целиком, а бэкенд про
  // флаг не знает и всё ещё может прислать эру в `tour`.
  if (isEraScene(sceneId) && !isStubEraScene(sceneId) && !IS_BALADY) {
    const parent = ERA_SCENE_PARENT[sceneId] ?? null;
    return {
      sceneId,
      labelKey: sceneId,
      iconSrc: parent ? SCENE_ICON_MAP[parent] ?? null : null,
    };
  }
  // 4. URL-id основной сцены — превращаем в backend-id, чтобы handleEnterScene
  // прошёл по main-ветке.
  if (KNOWN_SCENES.has(sceneId)) {
    const backendId = URL_ID_TO_BACKEND[sceneId] ?? sceneId;
    return {
      sceneId: backendId,
      labelKey: sceneId,
      iconSrc: SCENE_ICON_MAP[sceneId] ?? null,
    };
  }
  return null;
};
